import React, { useState, useEffect } from 'react';
import { DataGrid as MuiDataGrid, GridColumnVisibilityModel, GridColumns, GridCellParams } from '@mui/x-data-grid';
import { getJSONFromLocalStorage } from 'app/util/localStorageUtils';
import { processDate } from 'app/util/dateUtils';

//Data Grid interface. Visibility handling has been left as optional for occasions where it does not need
//to be tracked. Row click properties are for row clicks in the grids. Either can be changed where necessary.

interface DataGridProps<T> {
    data: T[] | undefined;
    columns: GridColumns;
    rowClickUrlPattern: string;
    excludeCells?: string[] | [];
    idField: string;
    defaultVisibilityModel?: GridColumnVisibilityModel;
    visibilityModelKey?: string;
    backgroundColour: string;
    hideFooter: boolean;
}

export default function DataGrid<T>({ data, columns, idField, defaultVisibilityModel, rowClickUrlPattern, excludeCells, visibilityModelKey, backgroundColour = '#FFF', hideFooter = false }: DataGridProps<T>) {
    //Format dates
    if(data) {
        formatDates(data);
    }
    //Get visibility model from local storage, if it exists
    const localStorageVisibilityModel: GridColumnVisibilityModel | null = getJSONFromLocalStorage<GridColumnVisibilityModel>(visibilityModelKey || null);
    //Setting the visibility model in state to either localStorageVisibilityModel if it exists or defaultVisibilityModel if provided.
    //Otherwise it's set to null
    const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel | null>(localStorageVisibilityModel ? localStorageVisibilityModel : defaultVisibilityModel || null);

    //Sets visibility model in local storage, whenever the state changes
    useEffect(() => {
        if (visibilityModelKey && visibilityModel) {
            localStorage.setItem(visibilityModelKey, JSON.stringify(visibilityModel));
        }
    }, [visibilityModel, visibilityModelKey])

    //Sets the state of visibility model, whenever it's changed, i.e whenever a column is shown or hidden.
    const handleColumnVisibilityChange = (newVisibilityModel: GridColumnVisibilityModel) => setVisibilityModel(newVisibilityModel);

    const handleCellClick = (param: GridCellParams) => {
        if (!excludeCells?.find((excludeCell) => {
            return excludeCell === param.field;
        }))
        {
            window.location.href = `${rowClickUrlPattern}${param.row[idField]}`;
        } 
    }

    if (!data || data.length === 0) {
        return(<span>No results found...</span>)
    }
    return(
        <div style={{ width: '100%', fontSize: '6px' }}>
        <MuiDataGrid
           getRowId={(row) => row[idField]}
           autoHeight={true}
           rows={data}
           pageSize={5}
           rowCount={5}
           onCellClick={handleCellClick}
           columns={columns}
           columnVisibilityModel={visibilityModel || undefined}
           onColumnVisibilityModelChange={(model) => handleColumnVisibilityChange(model)}    
           rowHeight={40}
           hideFooter={hideFooter}
           sx={{
               fontSize: 'small',
               "& .MuiDataGrid-row:hover": {
                   backgroundColor: backgroundColour,
                   cursor: 'pointer'
               }
           }}
       />
    </div>
    )
}

function formatDates<T>(data: T[]){
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            const propertyValue = data[key];
            if (propertyValue instanceof Date) {
                processDate(propertyValue);
            }
        }
        
    }
}